import { RefObject, useEffect, useRef, useState } from 'react';
import type Reaptcha from 'reaptcha';
import useForm from '~/client/hooks/form/useForm';
import useTranslation from '~/client/hooks/i18n/useTranslation';
import useSite from '~/client/hooks/useSite';
import useTermsUrl from '~/client/shared/TermsAndConditions/useTermsUrl';
import trackEvent from '~/shared/trackEvent';
import type FormValuesForFastSignup from './FormValuesForFastSignup';
import useHandleRegisterFastSignup from './useHandleRegisterFastSignup';
import useFormRegister from '../../FormRegister/useFormRegister';

type Props = {
  isPopover?: boolean;
  onSuccess?: () => void;
};
const useFormRegisterFastSignup = ({ onSuccess, isPopover }: Props) => {
  const [isRegistrationStart, setRegistrationStart] = useState(false);
  const [loading, setLoading] = useState(false);
  const { handlePayloadResponse, isCaptchaRequired } = useFormRegister({
    onSuccess,
    isPopover,
  });
  const { handleRegisterFastSignup } =
    useHandleRegisterFastSignup(isCaptchaRequired);
  const recaptchaRef = useRef<Reaptcha>(null);
  const { siteConfig } = useSite();

  const {
    formState,
    control,
    handleSubmit,
    errors,
    clearErrors,
    setErrors,
    required,
  } = useForm<FormValuesForFastSignup>({});
  const { isDirty, isValid, dirtyFields } = formState;
  const isSubmitDisabled = !isValid || !isDirty;
  const dirtyFieldsList = Object.keys(dirtyFields);
  const termsLink = useTermsUrl();

  const termsLabel = useTranslation('profile.signup.terms_text_accept', {
    values: {
      terms_url: termsLink,
      privacy_url: siteConfig.privacyPolicyLink,
    },
  });

  useEffect(() => {
    if (!isRegistrationStart && dirtyFieldsList.length) {
      setRegistrationStart(true);
      trackEvent({}, 'registrationStart');
    }
  }, [dirtyFieldsList.length, isRegistrationStart]);

  return {
    errors,
    control,
    handleSubmit: handleSubmit(async (value) => {
      setLoading(true);
      clearErrors();
      const payload = await handleRegisterFastSignup(value);
      if (payload?.errors) {
        setLoading(false);
      }
      return handlePayloadResponse(payload, setErrors);
    }),
    isCaptchaRequired,
    recaptchaRef: recaptchaRef as RefObject<Reaptcha>,
    isSubmitDisabled,
    required,
    termsLabel,
    loading,
  };
};
export default useFormRegisterFastSignup;
